// Editing the lifecycle/cost overlay for one integration (ff-api#1232).
// Only changed fields are sent; clearing an input clears the override and the
// YAML default applies again.
import React, { useMemo, useState } from 'react';
import { Loader2, Pencil, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getApiErrorMessage } from '@/client/utils/errors';
import { usePatchIntegrationMutation } from '@/client/api/integrations/integrations.query';
import {
  CostModel,
  Integration,
  IntegrationCost,
  IntegrationMetaPatch,
  IntegrationQuota,
  QuotaPeriod,
} from '@/client/types/integrations';

const COST_MODELS: CostModel[] = ['subscription', 'per_call', 'prepaid', 'free'];
const QUOTA_PERIODS: QuotaPeriod[] = ['day', 'week', 'month', 'quarter', 'year', 'term'];

interface QuotaRow {
  period: string;
  limit: string;
  label: string;
  valid_from: string;
  valid_until: string;
}

type TextField = 'owner' | 'plan' | 'limits' | 'notes';
type DateField = 'key_set_date' | 'key_expires' | 'renewal_date' | 'last_verified';

interface FormState {
  owner: string;
  plan: string;
  limits: string;
  notes: string;
  key_set_date: string;
  key_expires: string;
  renewal_date: string;
  last_verified: string;
  key_rotation_days: string;
  quota_used_pct: string;
  cost_model: string;
  cost_monthly: string;
  cost_unit: string;
  cost_currency: string;
  ceiling_requests: string;
}

const str = (v: unknown) => (v == null ? '' : String(v));

const toForm = (row: Integration): FormState => {
  const cost = typeof row.cost === 'object' && row.cost ? (row.cost as IntegrationCost) : null;
  return {
    owner: str(row.owner),
    plan: str(row.plan),
    limits: str(row.limits),
    notes: str(row.notes),
    key_set_date: str(row.key_set_date),
    key_expires: str(row.key_expires),
    renewal_date: str(row.renewal_date),
    last_verified: str(row.last_verified),
    key_rotation_days: str(row.key_rotation_days),
    quota_used_pct: str(row.quota_used_pct),
    cost_model: cost ? cost.model : typeof row.cost === 'string' && row.cost ? 'subscription' : '',
    cost_monthly: str(cost?.monthly_amount),
    cost_unit: str(cost?.unit_amount),
    cost_currency: str(cost?.currency ?? 'USD'),
    ceiling_requests: str(row.ceiling?.requests_per_month),
  };
};

const toQuotaRows = (row: Integration): QuotaRow[] =>
  (row.quotas ?? []).map((q) => ({
    period: q.period,
    limit: str(q.limit),
    label: str(q.label),
    valid_from: str(q.valid_from),
    valid_until: str(q.valid_until),
  }));

/** Only what actually changed, with '' meaning "clear the override". */
const buildPatch = (row: Integration, form: FormState, quotas: QuotaRow[]): IntegrationMetaPatch => {
  const initial = toForm(row);
  const patch: IntegrationMetaPatch = {};

  if (JSON.stringify(quotas) !== JSON.stringify(toQuotaRows(row))) {
    patch.quotas = quotas.length
      ? quotas.map((q) => {
          const quota: IntegrationQuota = {
            period: q.period as QuotaPeriod,
            limit: Number(q.limit),
          };
          if (q.label.trim()) quota.label = q.label.trim();
          if (q.valid_from) quota.valid_from = q.valid_from;
          if (q.valid_until) quota.valid_until = q.valid_until;
          return quota;
        })
      : null;
  }

  (['owner', 'plan', 'limits', 'notes'] as TextField[]).forEach((f) => {
    if (form[f] !== initial[f]) patch[f] = form[f].trim() || null;
  });
  (['key_set_date', 'key_expires', 'renewal_date', 'last_verified'] as DateField[]).forEach((f) => {
    if (form[f] !== initial[f]) patch[f] = form[f] || null;
  });
  if (form.key_rotation_days !== initial.key_rotation_days) {
    patch.key_rotation_days = form.key_rotation_days === '' ? null : Number(form.key_rotation_days);
  }
  if (form.quota_used_pct !== initial.quota_used_pct) {
    patch.quota_used_pct = form.quota_used_pct === '' ? null : Number(form.quota_used_pct);
  }
  if (form.ceiling_requests !== initial.ceiling_requests) {
    patch.ceiling =
      form.ceiling_requests === ''
        ? null
        : { ...(row.ceiling ?? {}), requests_per_month: Number(form.ceiling_requests) };
  }

  const costChanged =
    form.cost_model !== initial.cost_model ||
    form.cost_monthly !== initial.cost_monthly ||
    form.cost_unit !== initial.cost_unit ||
    form.cost_currency !== initial.cost_currency;
  if (costChanged) {
    if (!form.cost_model) {
      patch.cost = null;
    } else {
      const cost: IntegrationCost = { model: form.cost_model as CostModel, source: 'admin' };
      if (form.cost_monthly !== '') cost.monthly_amount = Number(form.cost_monthly);
      if (form.cost_unit !== '') cost.unit_amount = Number(form.cost_unit);
      if (form.cost_currency) cost.currency = form.cost_currency;
      patch.cost = cost;
    }
  }
  return patch;
};

const Input: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  overridden?: boolean;
}> = ({ label, value, onChange, type = 'text', placeholder, overridden }) => (
  <label className="block min-w-0">
    <span className="text-xs font-medium text-gray-500">
      {label}
      {overridden && <span className="ml-1 text-blue-600">· set here</span>}
    </span>
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 h-9 w-full rounded-lg border border-gray-200 px-2 text-sm outline-none focus:border-blue-400"
    />
  </label>
);

const ConfigEditor: React.FC<{ row: Integration; onDone: () => void }> = ({ row, onDone }) => {
  const [form, setForm] = useState<FormState>(() => toForm(row));
  const [quotas, setQuotas] = useState<QuotaRow[]>(() => toQuotaRows(row));
  const setQuota = (i: number, key: keyof QuotaRow) => (v: string) =>
    setQuotas((qs) => qs.map((q, n) => (n === i ? { ...q, [key]: v } : q)));
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const patchMutation = usePatchIntegrationMutation();

  const overridden = useMemo(() => new Set(row.overridden_fields ?? []), [row.overridden_fields]);
  const set = (key: keyof FormState) => (v: string) => setForm((f) => ({ ...f, [key]: v }));

  const patch = buildPatch(row, form, quotas);
  const dirty = Object.keys(patch).length > 0;

  const save = async () => {
    setFieldErrors({});
    try {
      await patchMutation.mutateAsync({ name: row.name, changes: patch });
      onDone();
    } catch (err) {
      // The API returns per-field messages; surface them next to the form.
      const data = (err as { data?: { fields?: Record<string, string> } })?.data;
      if (data?.fields) setFieldErrors(data.fields);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Owner"
          value={form.owner}
          onChange={set('owner')}
          placeholder="who answers for this vendor"
          overridden={overridden.has('owner')}
        />
        <Input
          label="Plan"
          value={form.plan}
          onChange={set('plan')}
          placeholder="e.g. Production 40k/mo"
          overridden={overridden.has('plan')}
        />
        <Input
          label="Limits"
          value={form.limits}
          onChange={set('limits')}
          placeholder="e.g. 5 req/s, 40k/mo"
          overridden={overridden.has('limits')}
        />
        <Input
          label="Quota used %"
          type="number"
          value={form.quota_used_pct}
          onChange={set('quota_used_pct')}
          overridden={overridden.has('quota_used_pct')}
        />
        <Input
          label="Key set on"
          type="date"
          value={form.key_set_date}
          onChange={set('key_set_date')}
          overridden={overridden.has('key_set_date')}
        />
        <Input
          label="Rotate every (days)"
          type="number"
          value={form.key_rotation_days}
          onChange={set('key_rotation_days')}
          overridden={overridden.has('key_rotation_days')}
        />
        <Input
          label="Key expires"
          type="date"
          value={form.key_expires}
          onChange={set('key_expires')}
          overridden={overridden.has('key_expires')}
        />
        <Input
          label="Renewal date"
          type="date"
          value={form.renewal_date}
          onChange={set('renewal_date')}
          overridden={overridden.has('renewal_date')}
        />
        <Input
          label="Last verified"
          type="date"
          value={form.last_verified}
          onChange={set('last_verified')}
          overridden={overridden.has('last_verified')}
        />
        <Input
          label="Our monthly ceiling (requests)"
          type="number"
          value={form.ceiling_requests}
          onChange={set('ceiling_requests')}
          overridden={overridden.has('ceiling')}
        />
      </div>

      <div className="rounded-lg border border-gray-100 p-3">
        <p className="text-xs font-medium text-gray-500">
          Quotas
          {overridden.has('quotas') && <span className="ml-1 text-blue-600">· set here</span>}
        </p>
        {quotas.map((q, i) => (
          <div key={i} className="mt-2 grid grid-cols-[7rem_1fr_1fr_1fr_1fr_auto] items-end gap-2">
            <label className="block">
              <span className="text-xs font-medium text-gray-500">Period</span>
              <select
                value={q.period}
                onChange={(e) => setQuota(i, 'period')(e.target.value)}
                className="mt-1 h-9 w-full rounded-lg border border-gray-200 px-2 text-sm outline-none focus:border-blue-400"
              >
                {QUOTA_PERIODS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>
            <Input label="Limit (requests)" type="number" value={q.limit} onChange={setQuota(i, 'limit')} />
            <Input label="Label" value={q.label} onChange={setQuota(i, 'label')} placeholder="e.g. Prepaid pack" />
            <Input label="Valid from" type="date" value={q.valid_from} onChange={setQuota(i, 'valid_from')} />
            <Input label="Valid until" type="date" value={q.valid_until} onChange={setQuota(i, 'valid_until')} />
            <Button
              size="sm"
              variant="outline"
              aria-label="Remove quota"
              onClick={() => setQuotas((qs) => qs.filter((_, n) => n !== i))}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
        <Button
          size="sm"
          variant="outline"
          className="mt-3"
          onClick={() =>
            setQuotas((qs) => [
              ...qs,
              { period: 'month', limit: '', label: '', valid_from: '', valid_until: '' },
            ])
          }
        >
          Add quota
        </Button>
        <p className="mt-2 text-xs text-gray-400">
          Usage is counted per period from our recorded calls. A term quota runs between its two
          dates (a prepaid pack, an annual contract). The monthly ceiling above counts as a month
          quota and is the one that raises alarms.
        </p>
      </div>

      <div className="rounded-lg border border-gray-100 p-3">
        <p className="text-xs font-medium text-gray-500">
          Cost
          {overridden.has('cost') && <span className="ml-1 text-blue-600">· set here</span>}
        </p>
        <div className="mt-2 grid grid-cols-4 gap-3">
          <label className="block">
            <span className="text-xs text-gray-500">Model</span>
            <select
              value={form.cost_model}
              onChange={(e) => set('cost_model')(e.target.value)}
              className="mt-1 h-9 w-full rounded-lg border border-gray-200 px-2 text-sm outline-none focus:border-blue-400"
            >
              <option value="">not set</option>
              {COST_MODELS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>
          <Input
            label="Monthly"
            type="number"
            value={form.cost_monthly}
            onChange={set('cost_monthly')}
            placeholder="subscription fee"
          />
          <Input
            label="Per call"
            type="number"
            value={form.cost_unit}
            onChange={set('cost_unit')}
            placeholder="unit price"
          />
          <Input label="Currency" value={form.cost_currency} onChange={set('cost_currency')} />
        </div>
        <p className="mt-2 text-xs text-gray-400">
          Spend uses this when the vendor reports none: a subscription counts the monthly fee,
          per-call multiplies by our metered calls.
        </p>
      </div>

      {Object.keys(fieldErrors).length > 0 && (
        <div className="rounded-lg border border-red-100 bg-red-50 p-3 text-sm text-red-700">
          {Object.entries(fieldErrors).map(([field, message]) => (
            <p key={field}>
              <span className="font-medium">{field}</span>: {message}
            </p>
          ))}
        </div>
      )}
      {patchMutation.isError && Object.keys(fieldErrors).length === 0 && (
        <div className="rounded-lg border border-red-100 bg-red-50 p-3 text-sm text-red-700">
          {getApiErrorMessage(patchMutation.error)}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button size="sm" onClick={save} disabled={!dirty || patchMutation.isPending}>
          {patchMutation.isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
          Save {dirty ? `(${Object.keys(patch).length})` : ''}
        </Button>
        <Button size="sm" variant="outline" onClick={onDone} disabled={patchMutation.isPending}>
          <X className="mr-2 h-3.5 w-3.5" />
          Cancel
        </Button>
        <span className="text-xs text-gray-400">
          Emptying a field clears the override and restores the registry default.
        </span>
      </div>
    </div>
  );
};

export const EditConfigButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <Button size="sm" variant="outline" onClick={onClick}>
    <Pencil className="mr-2 h-3.5 w-3.5" />
    Edit
  </Button>
);

export default ConfigEditor;
